/**
 * To allow the theme to support @mui/lab components (like `TabPanel` for example)
 * In order to benefit from the CSS overrides and default prop customization with the theme.
 * mui.com/material-ui/about-the-lab/#typescript
 */
import type {} from "@mui/lab/themeAugmentation";
import { alpha, createTheme, darken, getContrastRatio } from "@mui/material";
import { ThemeOptions } from "@mui/material/styles";
import {
  satoshi,
  satoshiBlack,
  satoshiBlackItalic,
  satoshiBold,
  satoshiBoldItalic,
  satoshiItalic,
  satoshiLight,
  satoshiLightItalic,
  satoshiMedium,
  satoshiMediumItalic,
  satoshiVariable,
  satoshiVariableItalic,
} from "./fonts/SatoshiFontObjs";
declare module "@mui/material/styles" {
  interface PaletteColor {
    grey: string;
    grey50: string;
    grey100: string;
    grey200: string;
    grey300: string;
    grey400: string;
    grey500: string;
    grey600: string;
    grey700: string;
    grey800: string;
    grey900: string;
  }

  interface SimplePaletteColorOptions {
    grey?: string;
    grey50?: string;
    grey100?: string;
    grey200?: string;
    grey300?: string;
    grey400?: string;
    grey500?: string;
    grey600?: string;
    grey700?: string;
    grey800?: string;
    grey900?: string;
  }

  interface Palette {
    greys: Palette["primary"];
    blue: Palette["primary"];
  }

  interface PaletteOptions {
    greys?: PaletteOptions["primary"];
    blue?: PaletteOptions["primary"];
  }
}

const blue = "#1C7BBD";
const themeOptions: ThemeOptions = {
  palette: {
    mode: "light",
    primary: {
      main: "#FF7900",
      dark: "#1D1D1B",
      grey: "#5E6167",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#E0E0E0",
      dark: "#1D1D1B",
    },
    blue: {
      main: blue,
      light: alpha(blue, 0.5),
      dark: darken(blue, 0.1),
      contrastText: getContrastRatio(blue, "#fff") > 4.5 ? "#fff" : "#1D1D1B",
    },
    greys: {
      main: "#5E6167",
      grey50: "#FAFAFA",
      grey100: "#F5F5F5",
      grey200: "#EEEEEE",
      grey300: "#E0E0E0",
      grey400: "#BDBDBD",
      grey500: "#9E9E9E",
      grey600: "#757575",
      grey700: "#616161",
      grey800: "#424242",
      grey900: "#212121",
    },
  },
  typography: (palette) => ({
    fontFamily: 'Satoshi, "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
    },
    body1: {
      lineHeight: "1.75rem",
    },
    body2: {
      lineHeight: "1.25rem",
    },
    subtitle1: {
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: "24px",
    },
    subtitle2: {
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: "22px",
      color: palette.primary.dark,
    },
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontSize: "1.5rem",
      fontWeight: "bold",
    },
    h6: {
      fontWeight: 700,
    },
    caption: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "19.92px",
      letterSpacing: "0.4",
      textAlign: "left",
    },
  }),
  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          marginTop: "1rem",
          marginBottom: "1rem",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          fontSize: "1rem",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          transitionDuration: "0.4s",
          ":hover": {
            textDecoration: "none",
            backgroundColor: darken(theme.palette.primary.main, 0.1),
          },
          "&.Mui-disabled": {
            background: "rgba(0, 0, 0, 0.12)",
            color: "rgba(0, 0, 0, 0.38)",
          },
        }),
        containedSecondary: ({ theme }) => ({
          textTransform: "none",
          fontSize: "1rem",
          backgroundColor: theme.palette.greys.grey300,
          color: theme.palette.primary.dark,
          transitionDuration: "0.4s",
          ":hover": {
            textDecoration: "none",
            backgroundColor: darken(theme.palette.greys.grey300, 0.1),
          },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: 0,
          color: theme.palette.primary.dark,
          backgroundColor: theme.palette.greys.grey300,
          "&.Mui-selected": {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          },
          "&:focus": {
            outline: "none",
          },
          "&:hover": {
            backgroundColor: `${darken(theme.palette.greys.grey300, 0.1)}`,
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          textDecoration: "none",
          transition: "color 1ms",
          ":hover": {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          textDecoration: "none",
          transition: theme.transitions.create(["color"], {
            duration: theme.transitions.duration.standard,
          }),
          ":hover": {
            textDecoration: "none",
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    // MuiSwitch: {
    //   styleOverrides: customSwitchStyles,
    // },
    MuiCssBaseline: {
      styleOverrides: {
        html: [
          { "@font-face": satoshi },
          { "@font-face": satoshiBold },
          { "@font-face": satoshiBlack },
          { "@font-face": satoshiBlackItalic },
          { "@font-face": satoshiItalic },
          { "@font-face": satoshiBoldItalic },
          { "@font-face": satoshiLight },
          { "@font-face": satoshiLightItalic },
          { "@font-face": satoshiMedium },
          { "@font-face": satoshiMediumItalic },
          { "@font-face": satoshiVariable },
          { "@font-face": satoshiVariableItalic },
        ],
      },
    },
  },
};
const memberTheme = createTheme(themeOptions);

export { memberTheme };
